/**
 * Learner Handbook — student-facing user documentation.
 *
 * Linked from the welcome email, the footer, and the header nav. Single
 * static page (no data fetch) so it's available before sign-in too — a
 * candidate can read it from the welcome-email link before clicking
 * "Start Module 1."
 *
 * Voice + structure mirror PrivacyPolicy.tsx / TermsOfService.tsx so the
 * three pages feel like one set of canonical references. Section IDs are
 * stable so the welcome email and future skills/help-center links can
 * deep-link to specific topics.
 */
import type * as React from "react";

export function LearnerHandbookPage(): React.JSX.Element {
  return (
    <article className="prose mx-auto max-w-3xl px-4 py-12 sm:px-6 sm:py-16">
      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        Learner handbook
      </p>
      <h1 className="mt-2 text-3xl font-bold text-foreground">
        CaiT Foundations Certificate — the user manual
      </h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Last updated 2026-06-02. Everything you need to know to earn the
        credential, in one page. Most clinicians complete the whole
        program in 6 to 8 hours across their own schedule.
      </p>

      {/* ===== TABLE OF CONTENTS ===== */}
      <nav
        aria-label="On this page"
        className="mt-8 rounded-lg border border-border bg-muted/40 p-4 text-sm"
      >
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          On this page
        </p>
        <ul className="mt-2 list-disc space-y-1 pl-5 text-foreground">
          <li><a href="#how-it-works" className="hover:text-primary">How the course is built</a></li>
          <li><a href="#each-lesson" className="hover:text-primary">What each lesson asks of you</a></li>
          <li><a href="#knowledge-checks" className="hover:text-primary">Knowledge checks (the 5-Q gates)</a></li>
          <li><a href="#module-quizzes" className="hover:text-primary">Module quizzes (the 10-Q gates)</a></li>
          <li><a href="#final-exam" className="hover:text-primary">The final exam</a></li>
          <li><a href="#capstone" className="hover:text-primary">The capstone</a></li>
          <li><a href="#certificate" className="hover:text-primary">Your certificate</a></li>
          <li><a href="#gamification" className="hover:text-primary">Badges, XP, streaks</a></li>
          <li><a href="#troubleshooting" className="hover:text-primary">If something goes wrong</a></li>
          <li><a href="#getting-help" className="hover:text-primary">Getting help</a></li>
        </ul>
      </nav>

      {/* ===== HOW IT WORKS ===== */}
      <section id="how-it-works" className="mt-10 space-y-4 text-foreground">
        <h2 className="text-xl font-semibold text-foreground">How the course is built</h2>
        <p>
          The CaiT Foundations Certificate is <strong>10 modules</strong>,
          each containing <strong>5 lessons</strong> (~4 minutes each) plus a
          <strong> module quiz</strong>. After all 10 module quizzes pass,
          you unlock the <strong>final exam</strong>. Pass the exam and the{" "}
          <strong>capstone</strong> unlocks. Submit a passing capstone and
          your <strong>certificate</strong> issues.
        </p>
        <p>
          Modules unlock in order: Module N+1 unlocks when you pass Module
          N&apos;s quiz at 80% or higher. You can revisit any unlocked
          lesson at any time, including after you finish the course.
        </p>
      </section>

      {/* ===== EACH LESSON ===== */}
      <section id="each-lesson" className="mt-10 space-y-4 text-foreground">
        <h2 className="text-xl font-semibold text-foreground">
          What each lesson asks of you
        </h2>
        <p>Every lesson page has three parts:</p>
        <ol className="ml-6 list-decimal space-y-2">
          <li>
            <strong>A short video</strong> (most are 30 – 90 seconds) — context
            and framing. You can pause, scrub, and re-watch.
          </li>
          <li>
            <strong>The lesson article</strong> — the actual content.
            Plain prose. Read it in full; the questions are written to
            test the shape of the argument, not your ability to memorize
            numbers.
          </li>
          <li>
            <strong>A 5-question knowledge check</strong> — see the next
            section.
          </li>
        </ol>
        <p>
          Above the &quot;Mark complete&quot; button you&apos;ll see a small{" "}
          <strong>Progress</strong> card with two indicators: scroll
          coverage (how far through the article you&apos;ve scrolled) and
          knowledge-check status. You can&apos;t mark a lesson complete
          until both pass.
        </p>
        <ul className="ml-6 list-disc space-y-1">
          <li>
            <strong>Scroll coverage:</strong> we count it as 100% once
            you&apos;ve scrolled to the bottom of the article body. The
            threshold is 80%. You don&apos;t have to read every word —
            but if you&apos;re trying to game it by jumping straight to
            the knowledge check, the questions will catch you.
          </li>
          <li>
            <strong>Knowledge check:</strong> you have to pass it
            (≥80% = 4 of 5 correct). Unlimited retakes; each retake
            draws a fresh question pool.
          </li>
        </ul>
      </section>

      {/* ===== KNOWLEDGE CHECKS ===== */}
      <section id="knowledge-checks" className="mt-10 space-y-4 text-foreground">
        <h2 className="text-xl font-semibold text-foreground">
          Knowledge checks (the 5-Q gates)
        </h2>
        <p>
          Five multiple-choice questions drawn from the lesson&apos;s pool.
          You&apos;re shown one question at a time with four options. There&apos;s
          no time limit — pause whenever you need to. After Submit you&apos;ll
          see each question&apos;s correct answer with a short rationale.
        </p>
        <p>
          <strong>To pass:</strong> 4 of 5 correct (80%). If you fail,
          click <em>Try again</em> for a fresh draw — questions and the
          order of options are reshuffled per attempt.
        </p>
        <h3 className="mt-6 text-base font-semibold text-foreground">
          If you have to step away mid-attempt
        </h3>
        <p>
          You can close the tab, lose Wi-Fi, or your laptop can run out of
          battery in the middle of a knowledge check — your work is saved.
          The next time you open the lesson and click{" "}
          <em>Take the knowledge check</em>, you&apos;ll land back on the
          same attempt with your prior answers pre-selected. A blue banner
          at the top says <strong>&quot;Picking up where you left off — your
          prior answers are pre-selected.&quot;</strong>
        </p>
        <p>
          If you&apos;d rather start over with fresh questions, click the{" "}
          <strong>Reset attempt</strong> button on that banner — it
          abandons the in-flight attempt and immediately starts a new
          one with a fresh question draw.
        </p>
      </section>

      {/* ===== MODULE QUIZZES ===== */}
      <section id="module-quizzes" className="mt-10 space-y-4 text-foreground">
        <h2 className="text-xl font-semibold text-foreground">
          Module quizzes (the 10-Q gates)
        </h2>
        <p>
          After all 5 lessons of a module, you take a 10-question module
          quiz. Questions are drawn from across the entire module&apos;s
          lesson pool — so reviewing the full module beforehand pays off.
        </p>
        <p>
          <strong>To pass:</strong> 8 of 10 correct (80%). Passing unlocks
          the next module.
        </p>
        <h3 className="mt-6 text-base font-semibold text-foreground">
          Taking the quiz before all lessons are marked complete
        </h3>
        <p>
          You can technically open the quiz before every lesson is marked
          complete — we&apos;ll show a yellow confirm panel reminding you{" "}
          <strong>you haven&apos;t finished all the lessons yet</strong>{" "}
          and asking whether to proceed anyway. Most learners do better
          finishing the lessons first, but the choice is yours.
        </p>
        <h3 className="mt-6 text-base font-semibold text-foreground">
          Retaking a failed quiz
        </h3>
        <p>
          You get to retake module quizzes freely — but{" "}
          <strong>three consecutive failures triggers a 1-hour cooldown</strong>{" "}
          on that module&apos;s quiz. Any pass resets the failure counter.
          The cool-down exists to nudge you toward re-reading rather than
          guessing through retries.
        </p>
        <h3 className="mt-6 text-base font-semibold text-foreground">
          The reflection prompt
        </h3>
        <p>
          After a passing module quiz we offer a short reflection prompt
          — 4 to 6 sentences on what the module changed about your
          thinking. It&apos;s optional (you can skip) and private (only
          you and admins can see it). Reflections are visible later on
          your <em>My Journal</em> page.
        </p>
      </section>

      {/* ===== FINAL EXAM ===== */}
      <section id="final-exam" className="mt-10 space-y-4 text-foreground">
        <h2 className="text-xl font-semibold text-foreground">The final exam</h2>
        <p>
          Unlocks after all 10 module quizzes pass. 50 questions drawn
          stratified across the 5 competency domains (clinical,
          ethical, technical, relational, outcomes).
        </p>
        <p>
          <strong>To pass:</strong> both conditions must hold:
        </p>
        <ul className="ml-6 list-disc space-y-1">
          <li><strong>≥80% overall</strong> (40 of 50 correct), AND</li>
          <li>
            <strong>≥60% in every domain</strong> (so a low score in any
            single competency blocks the credential — preventing a strong
            generalist with a weak ethics score from being certified).
          </li>
        </ul>
        <p>
          You can pause and resume during the exam; we don&apos;t enforce
          a hard timer in Phase 0, but elapsed time is logged.
        </p>
        <p>
          <strong>Cool-down:</strong> three consecutive failures triggers
          a <strong>24-hour</strong> cooldown (longer than the 1-hour
          module-quiz cool-down because the exam pool is broader and
          re-studying takes time). Any pass resets the counter.
        </p>
        <p>
          After you submit you&apos;ll see both your overall percentage
          AND your per-domain breakdown — including which domains met or
          missed the 60% floor. The breakdown also appears on your{" "}
          <em>Course home</em> in a card called &quot;Latest exam&quot;
          for any attempt you&apos;ve completed since 2026-06-02 — so you
          can come back later and still see what you scored.
        </p>
      </section>

      {/* ===== CAPSTONE ===== */}
      <section id="capstone" className="mt-10 space-y-4 text-foreground">
        <h2 className="text-xl font-semibold text-foreground">The capstone</h2>
        <p>
          Unlocks after a passing final exam. One open-response essay,{" "}
          <strong>1,500 – 3,000 words</strong>, applying the framework to
          a specific case the page describes. Most learners spend 60 – 90
          minutes drafting; you can save drafts and come back.
        </p>
        <p>
          Read the on-page case carefully — the rubric specifically
          penalizes generic, boilerplate answers. Specificity to the
          named client and decision points is one of the five graded
          criteria.
        </p>
        <h3 className="mt-6 text-base font-semibold text-foreground">
          How it&apos;s graded
        </h3>
        <p>
          Your essay is graded by an AI grader (Claude Sonnet 4.6) on
          five 1-to-5 criteria:
        </p>
        <ol className="ml-6 list-decimal space-y-1">
          <li>Clinical reasoning</li>
          <li>Ethical application</li>
          <li>Technical understanding</li>
          <li>Case specificity</li>
          <li>Professional communication</li>
        </ol>
        <p>
          To pass: composite score ≥4.0 AND every criterion ≥3. Submissions
          that the grader flags as borderline are queued for human review
          before the result lands.
        </p>
        <p>
          You get <strong>two attempts</strong> per enrollment. A saved
          draft does NOT count as an attempt — only the actual Submit
          does.
        </p>
        <p>
          When you submit, you&apos;ll see a banner: <em>&quot;Submitted
          for AI review — results in minutes. Watch your inbox.&quot;</em>{" "}
          You&apos;ll get an email with the result + your certificate (if
          you passed) within minutes.
        </p>
      </section>

      {/* ===== CERTIFICATE ===== */}
      <section id="certificate" className="mt-10 space-y-4 text-foreground">
        <h2 className="text-xl font-semibold text-foreground">Your certificate</h2>
        <p>
          A passing capstone with high grader confidence issues a
          certificate <em>automatically</em>. You&apos;ll get an email
          with:
        </p>
        <ul className="ml-6 list-disc space-y-1">
          <li>A PDF of the credential (your printable copy)</li>
          <li>A public <strong>verify URL</strong> in the form{" "}
            <code className="text-xs">verify.cait.academy/&lt;id&gt;</code>{" "}
            that anyone (employer, board, conference organizer) can hit to
            confirm the credential is active</li>
        </ul>
        <p>
          Certificates are valid for <strong>3 years</strong> from
          issuance. You&apos;ll get recertification reminder emails at 90,
          30, and 7 days before expiry.
        </p>
        <p>
          Lost the PDF? The cert link in the verify-URL email is
          permanent. If you can&apos;t find the email,{" "}
          <a href="mailto:support@clinicianassist.ai" className="text-primary hover:underline">
            email us
          </a>{" "}
          with the email you enrolled under and we&apos;ll resend.
        </p>
      </section>

      {/* ===== GAMIFICATION ===== */}
      <section id="gamification" className="mt-10 space-y-4 text-foreground">
        <h2 className="text-xl font-semibold text-foreground">
          Badges, XP, streaks
        </h2>
        <p>
          The credential is the whole point — the gamification layer is
          there to give you small momentum signals along the way. Visit{" "}
          <a href="/progress" className="text-primary hover:underline">
            My Progress
          </a>{" "}
          any time to see your current state.
        </p>
        <ul className="ml-6 list-disc space-y-1">
          <li>
            <strong>Badges</strong> (28 in the catalog): First Step, Module
            Completionist (one per module), Module Master (one per module
            at quiz-pass), All Lessons, Exam Passed, Certified, plus
            streak badges at 3 / 7 / 14 / 30 days.
          </li>
          <li>
            <strong>XP</strong>: awarded on lesson completion, module quiz
            pass, exam pass, capstone pass, and streak milestones. Totals
            shown on My Progress.
          </li>
          <li>
            <strong>Streak</strong>: consecutive days you&apos;ve been
            active. You start with 1 freeze token — if you miss a day,
            the freeze automatically protects your streak.
          </li>
        </ul>
        <p>
          None of this is required to earn the credential — you can
          ignore the My Progress page entirely and just take the course.
        </p>
      </section>

      {/* ===== TROUBLESHOOTING ===== */}
      <section id="troubleshooting" className="mt-10 space-y-4 text-foreground">
        <h2 className="text-xl font-semibold text-foreground">
          If something goes wrong
        </h2>

        <h3 className="mt-4 text-base font-semibold text-foreground">
          The &quot;Mark complete&quot; button is greyed out
        </h3>
        <p>
          Check the Progress card above it. One of two things is
          incomplete:
        </p>
        <ul className="ml-6 list-disc space-y-1">
          <li>Scroll coverage hasn&apos;t hit 80% — scroll farther down the article body.</li>
          <li>Knowledge check hasn&apos;t passed — take it (or retake it) and reach ≥4 of 5 correct.</li>
        </ul>
        <p>
          Sometimes after a quick reload the scroll coverage server-side
          can lag the client. Scroll slowly to the bottom once more —
          we send a beacon when you navigate away, and the next
          heartbeat will sync.
        </p>

        <h3 className="mt-4 text-base font-semibold text-foreground">
          I lost my place in a knowledge check
        </h3>
        <p>
          You didn&apos;t lose anything. Click <em>Take the knowledge
          check</em> again — the same attempt loads with your prior
          answers pre-selected, and a blue banner at the top confirms
          you&apos;re resuming. The <em>Reset attempt</em> button on that
          banner abandons the attempt if you want fresh questions
          instead.
        </p>

        <h3 className="mt-4 text-base font-semibold text-foreground">
          I got signed out
        </h3>
        <p>
          Click any link on the site and you&apos;ll bounce through the
          WorkOS sign-in page, then land back where you were. Your
          progress is server-side and never tied to your browser
          session.
        </p>

        <h3 className="mt-4 text-base font-semibold text-foreground">
          The Course-home page won&apos;t load past &quot;Loading...&quot;
        </h3>
        <p>
          Usually a stale session cookie. Sign out (or open an incognito
          window) and sign back in. If that doesn&apos;t fix it,{" "}
          <a href="mailto:support@clinicianassist.ai" className="text-primary hover:underline">
            email support
          </a>.
        </p>

        <h3 className="mt-4 text-base font-semibold text-foreground">
          My certificate didn&apos;t arrive
        </h3>
        <p>
          Certificate issuance runs automatically when your capstone
          grades pass with high confidence. If you got the &quot;passed&quot;
          email but no cert email, give it 10 minutes — there&apos;s a
          small queue. If it still hasn&apos;t arrived after a day,{" "}
          <a href="mailto:support@clinicianassist.ai" className="text-primary hover:underline">
            email us
          </a>{" "}
          with the email you enrolled under.
        </p>
      </section>

      {/* ===== HELP ===== */}
      <section id="getting-help" className="mt-10 space-y-4 text-foreground">
        <h2 className="text-xl font-semibold text-foreground">Getting help</h2>
        <p>
          Reply to any email from us, or write directly to{" "}
          <a href="mailto:support@clinicianassist.ai" className="text-primary hover:underline">
            support@clinicianassist.ai
          </a>
          . We answer within one business day.
        </p>
        <p>
          For privacy / data questions see our{" "}
          <a href="/privacy" className="text-primary hover:underline">
            Privacy Policy
          </a>
          . Program terms are on the{" "}
          <a href="/terms" className="text-primary hover:underline">
            Terms of Service
          </a>{" "}
          page.
        </p>
      </section>
    </article>
  );
}
